import apiClient from './ApiClient';

export class DateSlotRepository {
	constructor(client) {
		this.client = client;
		this.basePath = '/api/date-slots';
	}

	/**
	 * Get client availability (calendar view) - Public endpoint
	 * @param {string} startDate - Format YYYY-MM-DD
	 * @param {string} endDate - Format YYYY-MM-DD
	 * @param {string} companyId - Company UUID
 	 * @returns {Promise<{availability: Object.<string, {isAvailable: boolean, dateSlotId: string}>}>} Map of dates to availability objects
	 */
	async getClientAvailability(startDate, endDate, companyId) {
		return this.client.get(`${this.basePath}/client/availability`, {
			startDate,
			endDate,
			companyId
		});
	}

	/**
	 * Get available time slots for a specific date - Public endpoint
	 * @param {string} date - Format YYYY-MM-DD
	 * @param {string} companyId - Company UUID
	 * @returns {Promise<Array>} Array of available time slots
	 */
	async getClientTimeSlots(date, companyId) {
		return this.client.get(`${this.basePath}/client/time-slots`, { date, companyId });
	}

	/**
	 * Get admin calendar data (detailed) - Admin only
	 * @param {string} startDate - Format YYYY-MM-DD
	 * @param {string} endDate - Format YYYY-MM-DD
	 * @returns {Promise<{dates: Object}>} Detailed calendar information
	 */
	async getAdminCalendar(startDate, endDate) {
		return this.client.get(`${this.basePath}/admin/calendar`, {
			startDate,
			endDate
		});
	}

	/**
	 * Get admin time slots for a specific date - Admin only
	 * @param {string} date - Format YYYY-MM-DD
	 * @returns {Promise<Array>} Array of detailed time slots
	 */
	async getAdminTimeSlots(date) {
		return this.client.get(`${this.basePath}/admin/time-slots`, { date });
	}

	/**
	 * Get dashboard statistics - Admin only
	 * @param {string} startDate - Format YYYY-MM-DD
	 * @param {string} endDate - Format YYYY-MM-DD
	 * @returns {Promise<Object>} Statistics object
	 */
	async getStats(startDate, endDate) {
		return this.client.get(`${this.basePath}/admin/stats`, {
			startDate,
			endDate
		});
	}

	/**
	 * Bulk update date availability - Admin only
	 * @param {Array<string>} dates - Array of dates in YYYY-MM-DD format
	 * @param {boolean} isAvailable - Whether dates should be available
	 * @returns {Promise<{success: boolean}>}
	 */
	async bulkUpdateDeprecated(dates, isAvailable) {
		return this.client.post(`${this.basePath}/admin/bulk-update`, {
			dates,
			isAvailable
		});
	}

	async bulkUpdate(params) {
		return this.client.post(`${this.basePath}/admin/bulk-update-v2`, {
			startDate: params.startDate,
			endDate: params.endDate,
			daysOfWeek: params.daysOfWeek || [],
			recurringPattern: params.recurringPattern || 'none',
			workingHours: {
				startTime: params.workingHours.startTime,
				endTime: params.workingHours.endTime
			},
			action: params.action
		});
	}

	/**
	 * Set working hours for a specific date - Admin only
	 * @param {string} date - Format YYYY-MM-DD
	 * @param {string} startTime - Format HH:mm
	 * @param {string} endTime - Format HH:mm
	 * @returns {Promise<{success: boolean}>}
	 */
	async setWorkingHours(date, startTime, endTime) {
		return this.client.post(`${this.basePath}/admin/working-hours`, {
			date,
			startTime,
			endTime
		});
	}

	/**
	 * Copy schedule from source date to target dates - Admin only
	 * @param {string} sourceDate - Format YYYY-MM-DD
	 * @param {Array<string>} targetDates - Array of dates in YYYY-MM-DD format
	 * @returns {Promise<{success: boolean}>}
	 */
	async copySchedule(sourceDate, targetDates) {
		return this.client.post(`${this.basePath}/admin/copy-schedule`, {
			sourceDate,
			targetDates
		});
	}
}

export default new DateSlotRepository(apiClient);
